import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MessageCircle,
  Calendar,
  FileText,
  TrendingUp,
  UserPlus,
  Users,
  ChevronDown,
  ChevronRight,
  X,
  User,
  Circle,
  LogOut
} from 'lucide-react';

const SidebarComptable = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expertInfo, setExpertInfo] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const [expandedMenus, setExpandedMenus] = useState({
    'echanges': false
  });

  // Récupérer les informations de l'expert depuis localStorage
  useEffect(() => {
    const storedExpertInfo = localStorage.getItem('expert_info') || localStorage.getItem('expert_token') || localStorage.getItem('user');
    if (storedExpertInfo) {
      try {
        const parsed = JSON.parse(storedExpertInfo);
        setExpertInfo(parsed);
      } catch (error) {
        console.error('Erreur lors du parsing des informations expert:', error);
      }
    }
  }, []);

  // Fonction de déconnexion
  const handleLogout = async () => {
    setIsLoggingOut(true);

    // Nettoyer le localStorage
    localStorage.removeItem('expert_token');
    localStorage.removeItem('expert_info');
    localStorage.removeItem('user');
    localStorage.removeItem('role');

    await new Promise(resolve => setTimeout(resolve, 800));

    navigate('/loginexpert');

    if (onClose) onClose();
  };

  // Auto-expansion du menu parent basé sur l'URL
  useEffect(() => {
    if (location.pathname.startsWith('/dashboard-comptable')) {
      setExpandedMenus(prev => ({ ...prev, 'echanges': true }));
    }
  }, [location.pathname]);

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const menuItems = [
    {
      id: 'echanges',
      label: 'Échanges d\'informations',
      icon: MessageCircle,
      path: '/dashboard-comptable',
      subItems: [
        { id: 'echeances', label: 'Échéances fiscales', icon: Calendar, path: '/dashboard-comptable/echeances' },
        { id: 'fichiers', label: 'Échange de fichiers', icon: FileText, path: '/dashboard-comptable/fichiers' }
      ]
    },
    {
      id: 'donnees-mensuelles',
      label: 'Données mensuelles',
      icon: TrendingUp,
      path: '/donnees-mensuellescomptable'
    },
    {
      id: 'creer-client',
      label: 'Créer un client',
      icon: UserPlus,
      path: '/creer-client'
    },
    {
      id: 'liste-clients',
      label: 'Liste des clients',
      icon: Users,
      path: '/liste-clients'
    }
  ];

  const isItemActive = (item) => {
    if (item.path === location.pathname) return true;
    if (item.subItems && item.subItems.some(subItem => subItem.path === location.pathname)) {
      return true;
    }
    return false;
  };

  const handleMenuClick = (e, path, hasSubItems, itemId) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (hasSubItems) {
      toggleMenu(itemId);
    } else {
      navigate(path);
      if (onClose) onClose();
    }
  };

  const MenuItem = ({ item, level = 0 }) => {
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = expandedMenus[item.id];
    const active = isItemActive(item);
    const Icon = item.icon;

    if (hasSubItems) {
      return (
        <div className="menu-item-wrapper">
          <button
            className={`menu-button ${active ? 'active' : ''}`}
            onClick={(e) => handleMenuClick(e, item.path, true, item.id)}
            type="button"
          >
            <div className="menu-content">
              <Icon size={18} className="menu-icon" />
              <span className="menu-label">{item.label}</span>
            </div>
            <div className={`menu-chevron ${isExpanded ? 'expanded' : ''}`}>
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </div>
          </button>

          {isExpanded && (
            <div className="submenu">
              {item.subItems.map((subItem) => (
                <MenuItem key={subItem.id} item={subItem} level={level + 1} />
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="menu-item-wrapper">
        <button
          className={`menu-button ${level > 0 ? 'submenu-button' : ''} ${location.pathname === item.path ? 'active' : ''}`}
          onClick={(e) => handleMenuClick(e, item.path, false, item.id)}
          type="button"
        >
          <div className="menu-content">
            <Icon size={18} className="menu-icon" />
            <span className="menu-label">{item.label}</span>
          </div>
        </button>
      </div>
    );
  };

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="mobile-header">
          <h2 className="mobile-title">Menu Comptable</h2>
          <button onClick={onClose} className="close-button" type="button">
            <X size={20} />
          </button>
        </div>

        <nav className="navigation">
          <div className="menu-section">
            <h3 className="section-title">Gestion Client</h3>
            <div className="menu-list">
              {menuItems.map((item) => (
                <MenuItem key={item.id} item={item} />
              ))}
            </div>
          </div>
        </nav>

        {/* Informations de l'expert */}
        {expertInfo && (
          <div className="sidebar-user">
            <div className="user-avatar">
              <User size={24} />
            </div>
            <div className="user-info">
              <p className="user-name">{expertInfo.nom || expertInfo.name || expertInfo.email || 'Expert'}</p>
              <p className="user-email">{expertInfo.email}</p>
              <p className="user-role">Expert CINEF</p>
            </div>
            <div className="user-status-indicator">
              <Circle size={8} className="user-status" />
              <span className="status-text">En ligne</span>
            </div>
          </div>
        )}

        {/* Bouton de déconnexion */}
        <div className="sidebar-logout">
          <button
            className="logout-btn"
            onClick={handleLogout}
            disabled={isLoggingOut}
            type="button"
          >
            {isLoggingOut ? (
              <>
                <div className="spinner" />
                <span>Déconnexion...</span>
              </>
            ) : (
              <>
                <LogOut size={20} />
                <span>Se déconnecter</span>
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        .sidebar-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: 40;
          display: none;
        }

        .sidebar {
          position: fixed;
          top: 80px;
          left: 0;
          bottom: 0;
          width: 320px;
          background: linear-gradient(180deg, #ffffff 0%, #fafbfb 100%);
          border-right: 1px solid rgba(220, 53, 69, 0.1);
          box-shadow: 4px 0 20px rgba(220, 53, 69, 0.08);
          transform: translateX(0);
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          flex-direction: column;
          font-family: 'Inter', sans-serif;
          z-index: 50;
          overflow: hidden;
        }

        .mobile-header {
          display: none;
          align-items: center;
          justify-content: space-between;
          padding: 24px;
          background: linear-gradient(135deg, #dc3545 0%, #b02a37 100%);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .mobile-title {
          font-size: 20px;
          font-weight: 700;
          color: #ffffff;
          margin: 0;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
          letter-spacing: -0.3px;
        }

        .close-button {
          padding: 10px;
          color: #ffffff;
          background: rgba(255, 255, 255, 0.15);
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-button:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: scale(1.05);
        }

        .navigation {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 24px 0;
          scrollbar-width: thin;
          scrollbar-color: rgba(220, 53, 69, 0.3) transparent;
        }

        .navigation::-webkit-scrollbar {
          width: 6px;
        }

        .navigation::-webkit-scrollbar-thumb {
          background: rgba(220, 53, 69, 0.2);
          border-radius: 10px;
        }

        .menu-section {
          padding: 0 16px;
        }

        .section-title {
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          margin: 0 0 16px 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid #f1f5f9;
          letter-spacing: 0.5px;
        }

        .menu-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .menu-item-wrapper {
          width: 100%;
        }

        .menu-button {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 14px 16px;
          background: transparent;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 15px;
          font-weight: 500;
          color: #374151;
          text-align: left;
          font-family: 'Inter', sans-serif;
        }

        .menu-button:hover {
          background: rgba(220, 53, 69, 0.05);
          color: #dc3545;
          transform: translateX(4px);
        }

        .menu-button.active {
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.1), rgba(220, 53, 69, 0.05));
          color: #dc3545;
          font-weight: 600;
          border-left: 4px solid #dc3545;
          padding-left: 12px;
        }

        .menu-button.submenu-button {
          padding: 12px 16px 12px 44px;
          font-size: 14px;
          color: #6b7280;
        }

        .menu-button.submenu-button:hover {
          color: #dc3545;
        }

        .menu-button.submenu-button.active {
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.08), rgba(220, 53, 69, 0.03));
          color: #dc3545;
          border-left: 3px solid #dc3545;
          padding-left: 41px;
          font-weight: 600;
        }

        .menu-content {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }

        .menu-icon {
          flex-shrink: 0;
        }

        .menu-label {
          flex: 1;
          text-align: left;
        }

        .menu-chevron {
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.3s ease;
          flex-shrink: 0;
        }

        .submenu {
          margin-left: 8px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .submenu.collapsed {
          max-height: 0;
          opacity: 0;
        }

        .submenu.expanded {
          max-height: 500px;
          opacity: 1;
          padding: 4px 0;
        }

        /* User Section */
        .sidebar-user {
          padding: 16px;
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.03), rgba(0, 138, 68, 0.03));
          border-top: 1px solid rgba(0, 0, 0, 0.05);
          margin: 0 16px 8px 16px;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .user-avatar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, rgba(220, 53, 69, 0.1), rgba(0, 138, 68, 0.1));
          color: #dc3545;
          margin: 0 auto 12px auto;
          border: 2px solid rgba(220, 53, 69, 0.2);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .user-info {
          text-align: center;
          margin-bottom: 10px;
        }

        .user-name {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 4px 0;
          line-height: 1.3;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .user-email {
          font-size: 13px;
          color: #64748b;
          margin: 0 0 4px 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .user-role {
          font-size: 13px;
          color: #64748b;
          margin: 0;
          font-weight: 500;
        }

        .user-status-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding-top: 10px;
          border-top: 1px solid rgba(0, 0, 0, 0.05);
        }

        .user-status {
          color: #10b981;
          fill: #10b981;
          flex-shrink: 0;
        }

        .status-text {
          font-size: 12px;
          color: #10b981;
          font-weight: 600;
        }

        .sidebar-logout {
          padding: 16px;
          border-top: 1px solid rgba(0, 0, 0, 0.05);
        }

        .logout-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 0.875rem 1rem;
          border-radius: 0.5rem;
          border: none;
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          color: white;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(220, 38, 38, 0.2);
        }

        .logout-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
          box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
          transform: translateY(-1px);
        }

        .logout-btn:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(220, 38, 38, 0.2);
        }

        .logout-btn:disabled {
          cursor: not-allowed;
          opacity: 0.8;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 1023px) {
          .sidebar {
            top: 0;
            transform: translateX(-100%);
            width: 300px;
          }
          
          .sidebar.open {
            transform: translateX(0);
          }
          
          .sidebar-overlay {
            display: block;
          }
          
          .mobile-header {
            display: flex;
          }

          .sidebar-user {
            margin: 0 12px 8px 12px;
            padding: 12px;
          }

          .user-avatar {
            width: 48px;
            height: 48px;
          }
        }
      `}</style>
    </>
  );
};

export default SidebarComptable;